import { useEffect, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { useApi } from '@fortem-monorepo/react-spa'
import { FormError } from '@fortem-monorepo/react-ui'
import { SearchSelect } from '@fortem-monorepo/react-ui'
import { FormField, FormItem, FormLabel } from '@fortem-monorepo/react-ui'

import { FormFieldRow } from '@/components/forms/BookTradeForm/FormFieldRow'
import { CashSettlementDateField } from '@/pages/instruments/new-instrument/form-fields/CashSettlementDateField'
import { CounterpartyIdField } from '@/pages/instruments/new-instrument/form-fields/CounterpartyIdField'
import { CurrencyIdField } from '@/pages/instruments/new-instrument/form-fields/CurrencyIdField'
import { EffectiveDateFormField } from '@/pages/instruments/new-instrument/form-fields/EffectiveDateFormField'
import { FirstFloaterSettlementDateField } from '@/pages/instruments/new-instrument/form-fields/FirstFloaterSettlementDateField'
import { FloaterSpreadBpsField } from '@/pages/instruments/new-instrument/form-fields/FloaterSpreadBpsField'
import { FundIdField } from '@/pages/instruments/new-instrument/form-fields/FundIdField'
import { FundedReturnTypeField } from '@/pages/instruments/new-instrument/form-fields/FundedReturnTypeField'
import { FundingInstrumentFormField } from '@/pages/instruments/new-instrument/form-fields/FundingInstrumentFormField'
import { FundingReturnTypeField } from '@/pages/instruments/new-instrument/form-fields/FundingReturnTypeField'
import { MaturityDateField } from '@/pages/instruments/new-instrument/form-fields/MaturityDateField'
import { NotionalField } from '@/pages/instruments/new-instrument/form-fields/NotionalField'
import { PenultimateFloaterSettlementDateField } from '@/pages/instruments/new-instrument/form-fields/PenultimateFloaterSettlementDateField'
import { QuoteFactorFormField } from '@/pages/instruments/new-instrument/form-fields/QuoteFactorFormField'
import { SwapPaymentsYearlyField } from '@/pages/instruments/new-instrument/form-fields/SwapPaymentsYearlyField'
import { TradeIdField } from '@/pages/instruments/new-instrument/form-fields/TradeIdField'
import { UnitMultiplierFormField } from '@/pages/instruments/new-instrument/form-fields/UnitMultiplierFormField'
import { EquityOtcSchemaType } from '@/pages/instruments/new-instrument/schemas/equity-otc.schema'

export interface EquityOtcFormFieldsProps {}

export const EquityOtcFormFields: React.FC<EquityOtcFormFieldsProps> = ({}) => {
  const { setValue, trigger } = useFormContext<EquityOtcSchemaType>()

  const api = useApi()

  useEffect(() => {
    trigger()
  }, [])

  const autocallablesQuery = useQuery({
    queryKey: ['instruments', 'autocallables', { type: 'options' }],
    queryFn: () => api.instruments.getAutocallables(),
  })

  const autocallOptions = useMemo(() => {
    return autocallablesQuery.data ?? []
  }, [autocallablesQuery.data])

  return (
    <div className={'@container flex flex-col gap-4'}>
      <FormFieldRow>
        <FormField
          name="instrument_id"
          render={({ field: { value } }) => {
            return (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>Structurename</FormLabel>
                  <FormError />
                </div>
                <SearchSelect
                  loading={autocallablesQuery.isFetching}
                  options={autocallOptions}
                  value={value?.toString()}
                  placeholder={'Select an instrument'}
                  setValue={(val) =>
                    setValue('instrument_id', parseInt(val), { shouldValidate: true })
                  }
                />
              </FormItem>
            )
          }}
        />
        <FundIdField />
        <CurrencyIdField />
      </FormFieldRow>
      <FormFieldRow>
        <NotionalField placeholder={'1,000,000'} />
        <CounterpartyIdField />
        <TradeIdField placeholder={'Bank Trade ID'} />
        <EffectiveDateFormField />
      </FormFieldRow>
      <FormFieldRow>
        <MaturityDateField />
        <FirstFloaterSettlementDateField />
        <PenultimateFloaterSettlementDateField />
        <CashSettlementDateField />
      </FormFieldRow>
      <FormFieldRow>
        <FundingInstrumentFormField />
        <FloaterSpreadBpsField />
        <SwapPaymentsYearlyField />
      </FormFieldRow>
      <FormFieldRow>
        <FundingReturnTypeField />
        <FundedReturnTypeField />
        <UnitMultiplierFormField />
        <QuoteFactorFormField />
      </FormFieldRow>
    </div>
  )
}
